import { AutoComplete, Form, Input, InputNumber, message } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { AiOutlineEnter } from 'react-icons/ai';
import FormComponent from 'src/app/components/FormComponent/FormComponent';
import { convertToSelectOptionsWithId } from 'src/app/helpers/form.helper';

import { DefaultOptionType } from 'antd/es/select';
import OrderedTags from 'src/app/components/OrderedTagsComponent/OrderedTags';
import SearchableSelect from 'src/app/components/SearchableSelect/SearchableSelect';
import { IFormProps } from 'src/app/forms/form.config';
import { convertLocationIdentifiedToEditFormData } from 'src/app/forms/LocationIdentifiedForm/location-identified-form.config';
import {
  useCreateLocationMutation,
  useGetLocationsIdentifiedAllQuery,
  useUpdateLocationMutation,
} from 'src/app/redux/locations/locationsIdentifiedSlice';
import { useGetLocationsRecognizedQuery } from 'src/app/redux/locations/locationsRecognizedSlice';

const LocationIdentifiedForm: React.FC<IFormProps> = ({ form, recordId, destroyModal }) => {
  const [aliases, setAliases] = useState<string[]>([]);
  // const [deletedPictures, setDeletedPictures] = useState<string[]>([]);
  const [locationIdentifiedOptions, setLocationIdentifiedOptions] = useState<DefaultOptionType[]>([]);
  const [locationIdentifiedOptionsCopy, setLocationIdentifiedOptionsCopy] = useState<DefaultOptionType[]>([]);

  const { data: editData } = useGetLocationsIdentifiedAllQuery(undefined, {
    skip: !recordId,
    selectFromResult: ({ data }) => ({
      data: data?.rows.find((row) => row.id === recordId),
    }),
    refetchOnMountOrArgChange: true,
  });

  const { data: locationIdentifiedList } = useGetLocationsIdentifiedAllQuery({ isIdentified: true, order: 'name' });
  const { data: countryList } = useGetLocationsRecognizedQuery();

  useEffect(() => {
    if (editData) {
      form.setFieldsValue(convertLocationIdentifiedToEditFormData(editData));
      setAliases(editData.aliases.map((alias) => alias.value));
    }
  }, [editData]);

  // useEffect(() => {
  //   form.setFieldValue('aliases', aliases);
  // }, [aliases]);

  const [createLocation] = useCreateLocationMutation();
  const [updateLocation] = useUpdateLocationMutation();

  const onFinish = (values: any) => {
    if (!recordId) {
      createLocation({ ...values, aliases })
        .unwrap()
        .then(() => {
          message.success(t('success.added'));
          form.resetFields();
          if (destroyModal) destroyModal();
        });
      // .catch((err) => message.error(err?.response?.data?.message || t('errors.unknownError')));
    } else {
      updateLocation({ ...values, id: recordId, aliases })
        .unwrap()
        .then(() => {
          message.success(t('success.edited'));
          form.resetFields();
          if (destroyModal) destroyModal();
        });
      // .catch((err) => message.error(err?.response?.data?.message || t('errors.unknownError')));
    }
  };

  useEffect(() => {
    if (locationIdentifiedList) {
      const options: DefaultOptionType[] = locationIdentifiedList.rows.map((row) => ({
        label: row.name,
        value: row.name,
      }));
      setLocationIdentifiedOptions(options);
      setLocationIdentifiedOptionsCopy(options);
    }
  }, [locationIdentifiedList]);

  const onSearchName = (value: string) => {
    form.setFieldValue('name', value);
    setLocationIdentifiedOptions(
      locationIdentifiedOptionsCopy.filter((opt) =>
        opt.label?.toString().toLowerCase().startsWith(value.toLowerCase()),
      ),
    );
  };

  const onValuesChange = (values: any) => {
    if (values.name) {
      const foundLocation = locationIdentifiedList?.rows?.find((loc) => loc.name === values.name);
      if (!foundLocation) return;
      form.setFieldsValue({
        lat: foundLocation.lat,
        lng: foundLocation.lng,
        locationRecognizedId: foundLocation.locationRecognizedId,
      });
    }
  };

  return (
    <FormComponent options={{ form, onFinish, onValuesChange }}>
      <Form.Item label={t('forms.locationIdentifiedForm.name')} name="name" rules={[{ required: true }]}>
        <AutoComplete
          showSearch
          variant="borderless"
          disabled={!!recordId}
          onSearch={onSearchName}
          options={locationIdentifiedOptions}
        />
      </Form.Item>
      <Form.Item label={t('forms.locationIdentifiedForm.aliases')} name="aliases">
        <div style={{ display: 'flex', width: '100%' }}>
          <Input variant="borderless" onPressEnter={(e: any) => setAliases((prev) => [...prev, e.target.value])} />
          <div
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            onClick={() => {
              const alias = form.getFieldValue('aliases');
              if (!Array.isArray(alias)) setAliases((prev) => [...prev, alias]);
            }}
          >
            <span style={{ color: 'white' }}>{t('global.enter')}</span>
            <AiOutlineEnter size={26} color="white" />
          </div>
        </div>
      </Form.Item>

      <OrderedTags tags={aliases} setTags={setAliases} />

      <Form.Item label={t('forms.locationIdentifiedForm.latitude')} name="lat" rules={[{ required: true }]}>
        <InputNumber variant="borderless" style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item label={t('forms.locationIdentifiedForm.longitude')} name="lng" rules={[{ required: true }]}>
        <InputNumber variant="borderless" style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item
        label={t('forms.locationIdentifiedForm.country')}
        name="locationRecognizedId"
        rules={[{ required: true }]}
      >
        <SearchableSelect
          variant="borderless"
          options={convertToSelectOptionsWithId(countryList?.rows || [])}
          allowClear
        />
      </Form.Item>
      <Form.Item label={t('forms.locationIdentifiedForm.address')} name="address">
        <TextArea variant="borderless" />
      </Form.Item>
      <Form.Item label={t('forms.locationIdentifiedForm.description')} name="description">
        <TextArea variant="borderless" />
      </Form.Item>
      {/* <Form.Item label="Pictures" name="pictures" rules={[{ validator: interestImageValidator }]}>
        <UploadInterestImage
          form={form}
          recordId={recordId}
          interestType={InterestsTypes.Location}
          setDeletedPictures={setDeletedPictures}
        />
      </Form.Item> */}
    </FormComponent>
  );
};

export default LocationIdentifiedForm;
